import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import RmsFormTemplate from '../../components/RmsFormTemplate';
import {
  apiHfpTestGetBundle,
  apiHfpTestSaveDraft,
  apiHfpTestSubmit,
} from '../../utils/hfpTestApi';
import { loadHfpTestSession, formKeyForTileKey } from './hfpTestSession';

const DASHBOARD_ROUTE = '/rms/hfp-test/dashboard';

/**
 * HfpTestFormPage
 * ---------------
 * Staging-only form page for the HFP test site. Reuses the real RmsFormTemplate
 * verbatim, wiring its save/submit callbacks to the demo callables (which
 * delegate to the real form backend against the demo org). Behaviour — autosave,
 * resume, validation, review mode — is identical to the live service-user flow.
 */
export default function HfpTestFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { formKey: tileKey } = useParams();
  // Memoise so a fresh JSON.parse object each render doesn't churn the
  // formContext/onSaveStep/onComplete memos (and the template's downstream memos).
  const session = useMemo(() => loadHfpTestSession(), []);

  const formKey = useMemo(() => formKeyForTileKey(tileKey), [tileKey]);
  const roleSuffix = session?.roleSuffix;
  const patientRecordId = session?.patientRecordId;
  const email = session?.email;
  const instanceId = formKey && roleSuffix ? `${formKey}_${roleSuffix}` : null;

  const formContext = useMemo(() => ({
    pathway: 'cyp',
    initiatedBy: session?.role,
    currentUser: { role: roleSuffix },
  }), [session, roleSuffix]);

  const [bundle, setBundle] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!patientRecordId || !formKey || !instanceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiHfpTestGetBundle({ patientRecordId, formKey, instanceId, roleSuffix, email });
        if (cancelled) return;
        setBundle(res);
        setAnswers(res.answers || {});
        setError(null);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load this form.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patientRecordId, formKey, instanceId, roleSuffix, email]);

  const setAnswer = useCallback((questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const onSaveStep = useCallback(async (step, patchSubset, sectionProgress) => {
    await apiHfpTestSaveDraft({
      patientRecordId,
      formKey,
      instanceId,
      roleSuffix,
      email,
      patch: patchSubset,
      stepId: step?.questionId,
      context: formContext,
      sectionProgress,
    });
  }, [patientRecordId, formKey, instanceId, roleSuffix, email, formContext]);

  const onComplete = useCallback(async (finalAnswers) => {
    // Final defensive sync so the server has the latest answers before submit.
    try {
      await apiHfpTestSaveDraft({
        patientRecordId,
        formKey,
        instanceId,
        roleSuffix,
        email,
        patch: finalAnswers || answers || {},
        stepId: '__final_sync__',
        context: formContext,
      });
    } catch (e) {
      // Non-blocking — submit re-validates server-side.
      console.warn('[HFP test] final sync failed (non-blocking)', e);
    }
    await apiHfpTestSubmit({
      patientRecordId,
      formKey,
      instanceId,
      roleSuffix,
      email,
      idempotencyKey: `${instanceId}-submit`,
    });
    // Back to the overview (mirrors the real service-user flow after submit).
    navigate(DASHBOARD_ROUTE);
    setTimeout(() => { try { window.scrollTo({ top: 0, behavior: 'instant' }); } catch { window.scrollTo(0, 0); } }, 0);
  }, [patientRecordId, formKey, instanceId, roleSuffix, email, formContext, answers, navigate]);

  const onHome = useCallback(() => navigate(DASHBOARD_ROUTE), [navigate]);

  if (!session) return <Navigate to="/rms/hfp-test" replace />;
  // Corrupt/partial session (no role or record) — send back to entry rather than
  // hang on the loading spinner (the load effect no-ops without a valid instanceId).
  if (!roleSuffix || !patientRecordId) return <Navigate to="/rms/hfp-test" replace />;
  if (!formKey) return <Navigate to={DASHBOARD_ROUTE} replace />;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex items-center justify-center">
        <div className="text-gray-700">Loading… Preparing form</div>
      </div>
    );
  }
  if (error || !bundle?.schema) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex items-center justify-center">
        <div className="text-red-600">{error || 'Could not load this form.'}</div>
      </div>
    );
  }

  const searchParams = new URLSearchParams(location.search || '');
  const urlModeParam = searchParams.get('mode');
  const initialMode = bundle.status === 'submitted' || urlModeParam === 'review' ? 'review' : 'questions';

  return (
    <div className="hfp-test-form hfp-compact-form">
      <RmsFormTemplate
        schema={bundle.schema}
      answers={answers}
      setAnswer={setAnswer}
      onSaveStep={onSaveStep}
      onComplete={onComplete}
      initialStepId={bundle.lastUnanswered || bundle.lastStepId || null}
      initialMode={initialMode}
      formContext={formContext}
      brandBackTo={DASHBOARD_ROUTE}
      serverSectionProgress={bundle.sectionProgress}
      prefillSources={null}
      onConfirmPrefills={() => {}}
      showBrand={false}
      showHome
      onHome={onHome}
      // HFP runs on RMS form machinery today but may become its own product;
      // labelling the rows now means that split costs no renames or backfill.
      analyticsContext={{ patientRecordId, product: 'hfp' }}
      orgId={session.orgId}
      patientRecordId={patientRecordId}
      wasSubmitted={bundle.status === 'submitted'}
      />
    </div>
  );
}
